import React, { Component } from 'react';
import { Container, Nav, Alert } from 'react-bootstrap';
import { Link } from "react-router-dom";
import { GoOrganization, GoGear } from "react-icons/go";
import ReactGA from 'react-ga';
import Profile from "components/profile/profile";
import Organizations from "components/profile/organizations";
import Settings from "./settings/settings";
import AccountTypes from 'context/accountTypes';
import Title from 'context/title';
import Utils from 'context/utils';
import { isNavKeyboardSelect } from "context/keyboard";

export default class Page extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: null,
      orgs: null,
      notFound: false,
    };

    this.profileInterface = {
      loadProfile: this.loadProfile
    };

    // Guards title writes from late-arriving promise callbacks (see componentWillUnmount).
    this.unmounted = false;
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  componentDidMount() {
    // The alias is in the route params, so the tab is right from first paint; loadProfile upgrades
    // it to the user's name once that resolves.
    Title.set(this.props.match.params.userId);

    ReactGA.pageview('/profile/user');

    this.loadData();
  }

  loadData() {
    // Query the user profile. An account cannot own organizations, so the org request is known in
    // advance to come back empty for one — it depends on the user's type and is issued only once the
    // profile resolves, instead of firing in parallel with a request whose answer is already known.
    this.loadProfile().then(user => {
      if (user && AccountTypes.isHuman(user)) this.loadOrgs();
    });
  }

  loadProfile = () => {
    const userId = this.props.match.params.userId;
    return this.props.app.api.usersIdGetNoCache(userId)
      .then(response => {
        const user = response.data.user;
        this.setState({ user });
        // A user who navigates away (e.g. via the footer) before this resolves must not have the
        // page they landed on next overwritten by this stale write.
        if (!this.unmounted) Title.set(Utils.displayName(user, userId));
        return user;
      }, error => {
        this.setState({ notFound: true });
        if (!this.unmounted) Title.setNotFound();
      });
  }

  loadOrgs() {
    const userId = this.props.match.params.userId;
    this.props.app.api.users_id_orgs_get(userId)
      .then(response => {
        this.setState({ orgs: response.data.organizations });
      }, error => {});
  }

  // Where each tab goes. Read by the links themselves and by the arrow-key handler below, so the two
  // can never point at different places. An account cannot own organizations, and settings render
  // only for the account's own profile, so a tab that isn't rendered has no entry here either — the
  // arrow-key handler below looks up the key it's given and does nothing when it comes back empty.
  tabPaths() {
    const userId = this.props.match.params.userId;
    const paths = {};
    if (!AccountTypes.isAccount(this.state.user)) paths.organizations = `/p/${userId}`;
    if (this.props.app.api.isSelfUser(this.state.user)) paths.settings = `/p/${userId}/settings`;
    return paths;
  }

  // The tabs are links and navigate on their own; arrow-key traversal belongs to react-bootstrap,
  // which reports the tab it reached here instead of activating its link.
  handleKeyboardTraverse = (key, event) => {
    if (!isNavKeyboardSelect(event)) return;
    const path = this.tabPaths()[key];
    if (path) this.props.history.push(path);
  };

  render() {
    if ( this.state.notFound ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="warning">Requested user has not been found.</Alert>
          </Container>
        </>
      );
    }

    if ( !this.state.user ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="primary">Loading user profile...</Alert>
          </Container>
        </>
      );
    }

    // An account cannot own organizations, so that tab and its pane are dropped for one entirely; the
    // section the page opens on then defaults to settings instead of the now-absent organizations,
    // computed here from the already-loaded user rather than a post-load redirect.
    const showOrganizationsTab = AccountTypes.isHuman(this.state.user);
    const showSettingsTab = this.props.app.api.isSelfUser(this.state.user);
    const defaultSection = showOrganizationsTab ? "organizations" : "settings";
    const section = this.props.match.params.section || defaultSection;
    const tabPaths = this.tabPaths();

    return (
      <>
        <Profile
          app={this.props.app}
          user={this.state.user}
        />
        <br/>

        {(showOrganizationsTab || showSettingsTab) &&
          <Container className="blob-header-row main-tabs">
            <Nav
              variant="tabs"
              as="nav"
              role="tablist"
              activeKey={section}
              onSelect={this.handleKeyboardTraverse}
            >
              {showOrganizationsTab &&
                <Nav.Link
                  eventKey="organizations"
                  role="tab"
                  className="nav-item"
                  as={Link}
                  to={tabPaths.organizations}
                ><GoOrganization/> Organizations</Nav.Link>
              }
              {showSettingsTab &&
                <Nav.Link
                  eventKey="settings"
                  role="tab"
                  className="nav-item"
                  as={Link}
                  to={tabPaths.settings}
                ><GoGear/> Settings</Nav.Link>
              }
            </Nav>
          </Container>
        }

        {showOrganizationsTab &&
          <div className={"organizations" === section ? "" : "hidden"}>
            <Organizations
              app={this.props.app}
              user={this.state.user}
              orgs={this.state.orgs}
            />
          </div>
        }

        {showSettingsTab &&
          <div className={"settings" === section ? "" : "hidden"}>
            <Settings
              app={this.props.app}
              match={this.props.match}
              user={this.state.user}
              userId={this.props.match.params.userId}
              profileInterface={this.profileInterface}
            />
          </div>
        }

        <br/>
      </>
    )
  }
}
